import { IMsalContext, useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { Constants } from "../../../Models/Constants";
import { IDataSample } from "../../../Models/IDataSample";
import { IDesignSession } from "../../../Models/IDesignSession";
import { ISMEDesignguidelines } from "../../../Models/ISMEDesignGuidelines";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { ApiService } from "../../../Services/ApiService";
import logger from '../../../Services/LoggingService';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Alert,
  Paper,
  Tooltip,
  useTheme
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';

/**
 * SME Design Guidelines - Material UI Version
 *
 * @export
 * @param {{
 *     designSession?: IDesignSession,
 *     dataSample?: IDataSample,
 *     unitConfiguration?: IUnitConfiguration,
 *     setDesignGuidelineId?: (id: bigint) => void,
 *     isReadOnly: boolean
 * }} props
 * @return {*} 
 */
export const SMEDesignGuidelines: React.FC<{
  designSession?: IDesignSession,
  dataSample?: IDataSample,
  unitConfiguration?: IUnitConfiguration,
  setDesignGuidelineId?: (id: bigint) => void,
  isReadOnly: boolean
}> = (props) => {
  const theme = useTheme();
  const ctx: IMsalContext = useMsal();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [SMEdesignSuggestion, setSMEDesignSuggestion] = useState<ISMEDesignguidelines>();
  const [designSuggestion, setDesignSuggestion] = useState<string>("");

  // Consolidated styles object
  const styles = {
    // Main container styles
    paperContainer: {
      padding: theme.spacing(1),
    },
    
    // Typography styles
    mainTitle: {
      paddingBottom: theme.spacing(1),
    //   fontWeight: 600,
      color: theme.palette.text.primary
    },
    fieldLabel: {
      marginBottom: theme.spacing(0.5),
      fontWeight: 500,
      color: theme.palette.text.secondary
    },
    
    // Alert and message styles
    alertContainer: {
      marginBottom: theme.spacing(2)
    },
    warningAlert: {
      marginBottom: theme.spacing(2),
      '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'underline'
      }
    },
    
    // Content sections
    contentSection: {
      paddingTop: theme.spacing(1)
    },
    buttonSection: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'flex-end'
    },
    
    // Dialog styles
    dialogTitle: {
      borderTop: `4px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.grey[100],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(2)
    },
    dialogTitleText: {
      fontWeight: 600,
      color: theme.palette.text.primary
    },
    dialogContent: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2)
    },
    dialogActions: {
      paddingBottom: theme.spacing(2),
      justifyContent: 'center'
    },
    
    // Form layout styles
    formGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2)
    },
    formField: {
      width: 'calc(50% - 8px)',
      display: 'flex',
      flexDirection: 'column'
    },
    fullWidthField: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    
    // Input field styles
    textField: {
      marginTop: theme.spacing(0.5)
    },
    readOnlyTextField: {
      marginTop: theme.spacing(1),
      '& .MuiInputBase-root': {
        maxHeight: 743,
        overflow: 'auto'
      },
      '& .MuiInputBase-input': {
        cursor: 'default'
      }
    },
    multilineField: {
      '& .MuiInputBase-root': {
        minHeight: theme.spacing(12)
      }
    },
    
    // Button styles
    primaryButton: {
      minWidth: theme.spacing(15)
    },
    submitButton: {
      minWidth: theme.spacing(20)
    },
    
    // Icon and tooltip styles
    infoIconContainer: {
      display: 'flex',
      alignItems: 'center'
    },
    infoIcon: {
      marginLeft: theme.spacing(1),
      cursor: 'pointer'
    },
    closeIconButton: {
      padding: theme.spacing(0.5)
    },
    
    // Utility styles
    flexRow: {
      display: 'flex',
      alignItems: 'center'
    },
    fullWidth: {
      width: '100%'
    },
    spacingTop1: {
      paddingTop: theme.spacing(1)
    },
    spacingBottom1: {
      paddingBottom: theme.spacing(1)
    },
    spacingTop2: {
      paddingTop: theme.spacing(2)
    }
  };

  useEffect(() => {
    if (props.designSession !== undefined && props.designSession !== null) {
      if (props.designSession!.smeDesignGuideLineId === null && (props.designSession.source === Constants.SOURCE_UNASSIGNED || props.designSession.source === null)) {
        getSMEDesignGiudelines(props.unitConfiguration!);
      }
      else if (props.designSession!.smeDesignGuideLineId !== null) {
        getSMEDesignSuggestionForSession(props.designSession!.smeDesignGuideLineId!);
      }
    }

    if (props.dataSample !== undefined && props.dataSample !== null) {
      if (props.dataSample!.smeDesignGuideLineId === null && props.dataSample.dataSampleStatus !== Constants.DATASAMPLESTATUS_Done) {
        getSMEDesignGiudelines(props.unitConfiguration!);
      }
      else if (props.dataSample!.smeDesignGuideLineId !== null) {
        getSMEDesignSuggestionForSession(props.dataSample!.smeDesignGuideLineId!);
      }
    }
  }, []);

  /**
   * Get SME design guidelines for the session
   *
   * @param {bigint} id
   */
  async function getSMEDesignSuggestionForSession(id: bigint) {
    const response = await apiService.getAsync(`settings/smedesignguidelines/latest`, props.unitConfiguration!);
    const designguideline: ISMEDesignguidelines = await response.json();
    setSMEDesignSuggestion(designguideline);
    props.setDesignGuidelineId?.(designguideline.id!);
  }

  /**
   * Show modal for SME design guidelines
   */
  function showModalForSMeDesignGuidelines() {
    setIsModalOpen(true);
  }

  /**
   * Hide modal
   */
  function hideModal() {
    setIsModalOpen(false);
  }

  /**
   * Add new design guideline
   */
  const addNewDesignGuideLine = async () => {
    let body: any = {}
    body.designSuggestion = designSuggestion;

    hideModal();
    let response = await apiService.postAsync(body, `settings/createappenddesignsuggestion`, props.unitConfiguration);

    if (!response.ok) {
      const res = await response.text();
      throw Error(`Could not send response. ${response.status} ${res}`);
    }
    getSMEDesignGiudelines(props.unitConfiguration!);
  }

  /**
   * Get SME design guidelines
   *
   * @param {IUnitConfiguration} unitConfig
   */
  async function getSMEDesignGiudelines(unitConfig: IUnitConfiguration) {
    const response = await apiService.getAsync(`settings/smedesignguidelines/latest`, unitConfig);
    if (!response.ok) {
      if (response.status === 404) {
        logger.warn("SME Design Guidelines not found (404)", {
          component: 'SMEDesignGuidelines',
          method: 'getSMEDesignGiudelines'
        });
      }
      else {
        throw new Error('Could not get SME design guidelines. ' + response.status + ' ' + response.statusText);
      }

      setShowMessage(true);
    }
    else {
      const designguideline: ISMEDesignguidelines = await response.json();
      setSMEDesignSuggestion(designguideline);
      props.setDesignGuidelineId?.(designguideline.id!);
    }
  }

  return (
    <Paper
      elevation={0}
      sx={styles.paperContainer}
    >
      <Typography variant="h3" fontWeight="bold" sx={styles.mainTitle}>SME Design Guidelines</Typography>
      
      {showMessage && (
        <Alert 
          severity="warning" 
          onClose={() => setShowMessage(false)}
          sx={styles.alertContainer}
        >
          {Constants.SMEDesignguidelineNotFoundforGivenconfiguration}<br />
          To add SME design guidelines, <a href={'/admin'}>go to settings</a>
        </Alert>
      )}

      {!props.isReadOnly && props.designSession !== undefined && props.designSession !== null && (
        <Box>
          <Box sx={styles.contentSection}>
            {SMEdesignSuggestion === undefined && showMessage && (
              <Alert 
                severity="warning" 
                onClose={() => setShowMessage(false)}
                sx={styles.warningAlert}
              >
                {Constants.SMEDesignguidelineNotFoundforGivenconfiguration}<br />
                {Constants.ClickOnAddButtonToAddORAppendDesignGuidelines}
              </Alert>
            )}
          </Box>
          
          <Box sx={styles.buttonSection}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={showModalForSMeDesignGuidelines}
              sx={styles.primaryButton}
            >
              Add/Append
            </Button>
          </Box>

          <Dialog 
            open={isModalOpen} 
            onClose={hideModal} 
            maxWidth="md" 
            fullWidth
          >
            <DialogTitle sx={styles.dialogTitle}>
              <Typography sx={styles.dialogTitleText}>Create or Append design suggestion</Typography>
              <IconButton 
                onClick={hideModal} 
                size="small"
                edge="end"
                sx={styles.closeIconButton}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={styles.dialogContent}>
              <Box sx={styles.formGrid}>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Industry</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.industry}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Subindustry</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.subIndustry}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Application</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.application}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Subapplication</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.subApplication}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Process stage</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.processStage}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Product line</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.productLine}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant="body2" sx={styles.fieldLabel}>Alfa laval position</Typography>
                  <TextField 
                    fullWidth
                    size="small"
                    value={props.unitConfiguration!.alfaLavalPosition}
                    margin="dense"
                    sx={styles.textField}
                  />
                </Box>
                <Box sx={styles.fullWidthField}>
                  <Box sx={styles.infoIconContainer}>
                    <Typography variant="body2" sx={styles.fieldLabel}>Design suggestion</Typography>
                    <Tooltip title="Design suggestion will be appended to existing suggestion.">
                      <InfoIcon 
                        fontSize="small" 
                        color="primary" 
                        sx={styles.infoIcon}
                      />
                    </Tooltip>
                  </Box>
                  <TextField 
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={SMEdesignSuggestion?.designSuggestion}
                    onChange={(e) => setDesignSuggestion(e.target.value)}
                    margin="dense"
                    sx={{...styles.textField, ...styles.multilineField}}
                  />
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions sx={styles.dialogActions}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<CheckIcon />}
                onClick={addNewDesignGuideLine}
                sx={styles.submitButton}
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={20}
        value={SMEdesignSuggestion?.designSuggestion || ''}
        InputProps={{ readOnly: true }}
        sx={styles.readOnlyTextField}
      />
    </Paper>
  );
}; 